"use client";

/**
 * Schedule Label Filter
 *
 * Shared chip filter for the schedule's labels, used by both the inline ScheduleRenderer and the
 * focused full-page calendar. Renders as a group of toggle buttons (`aria-pressed`) rather than an
 * ARIA radiogroup — a radiogroup would require arrow-key roving we don't implement, and toggle
 * buttons match the calendar's day-tab pattern. The active chip uses a solid label-color background
 * with luminance-picked text so contrast holds for any admin-configured color.
 */

import type React from "react";

import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import { SCHEDULE_DEFAULT_LABEL_COLOR } from "../../../../_logic/constants";
import type { ScheduleLabelRow } from "../../../../_logic/types/db-types";

interface ScheduleLabelFilterProps {
  /** Labels to render as filter chips. Renders nothing when empty. */
  labels: ScheduleLabelRow[];
  /** Currently-selected label id, or `null` for the "All" chip. */
  activeLabelId: number | null;
  /** Called with the next selection (`null` = All). */
  onChange: (labelId: number | null) => void;
  /** Brand color used for the active "All" chip. */
  primaryColor: string;
  /** Extra classes for the chip container (e.g. alignment overrides). */
  className?: string;
  /** Render smaller chips for tight chrome, such as the focused full-page schedule. */
  compact?: boolean;
  /** Keep labels on one horizontal scrolling row instead of wrapping. */
  scrollable?: boolean;
}

/**
 * Renders the label filter chips.
 *
 * @param props - See ScheduleLabelFilterProps.
 * @returns The chip group, or `null` when there are no labels.
 */
export function ScheduleLabelFilter({
  labels,
  activeLabelId,
  onChange,
  primaryColor,
  className = "",
  compact = false,
  scrollable = false,
}: ScheduleLabelFilterProps): React.ReactElement | null {
  if (labels.length === 0) return null;

  const chipClass = (isActive: boolean): string =>
    `shrink-0 touch-manipulation rounded-full font-medium transition-colors ${
      compact ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"
    } ${isActive ? "" : "text-[color:var(--lp-text,#4b5563)] hover:bg-[color:var(--lp-surface-hover,#f3f4f6)]"}`;
  const containerClass = scrollable
    ? `flex min-w-0 items-center gap-1 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`
    : `flex flex-wrap items-center gap-1 ${className}`;

  return (
    <div
      className={containerClass}
      role="group"
      aria-label="Filter by label"
      style={scrollable ? { scrollbarWidth: "none" } : undefined}
    >
      <button
        type="button"
        aria-pressed={activeLabelId === null}
        onClick={() => onChange(null)}
        className={chipClass(activeLabelId === null)}
        style={
          activeLabelId === null
            ? { backgroundColor: primaryColor, color: getButtonTextColor(primaryColor) }
            : undefined
        }
      >
        All
      </button>
      {labels.map((label) => {
        const color = label.color || SCHEDULE_DEFAULT_LABEL_COLOR;
        const isActive = activeLabelId === label.id;
        return (
          <button
            type="button"
            key={label.id}
            aria-pressed={isActive}
            onClick={() => onChange(label.id)}
            className={chipClass(isActive)}
            style={isActive ? { backgroundColor: color, color: getButtonTextColor(color) } : undefined}
          >
            {label.name}
          </button>
        );
      })}
    </div>
  );
}
